import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';
import {VelocityTransitionGroup} from 'velocity-react';

import OEThemeIndicatorView from './oe-theme-indicator-view';
import {OEWindowComponent} from '../../lib/components/oe-window';
import OEToolbox from '../../lib/tools/oe-toolbox';
import retardUpdate from '../../lib/react/update-retarder';

export default class OEThemeWaitingController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.show_ = 0;

        this.state = {
            show: 0,
            progress: 0,
            options: this.determineActiveOptions()
        };

        this.onWillAppear = this.onWillAppear.bind(this);
        this.onDidAppear = this.onDidAppear.bind(this);
        this.onWillDisappear = this.onWillDisappear.bind(this);
        this.onDidDisappear = this.onDidDisappear.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if(!OEToolbox.shallowEqual(nextProps.options, this.props.options, true))  {
            this.setState({options: this.determineActiveOptions(nextProps)});
        }
    }

    show(options) {
        let oldShow = this.show_;
        this.show_ += 1;

        retardUpdate(this, () => {

            this.setState({show: this.show_});

            if(oldShow === 0)    {
                this.setProgress(0);
                this.options = clone(options);
                this.setState({options: this.determineActiveOptions()});
            }
        });
    }

    hide() {
        this.show_ = Math.max(0, this.show_ - 1);
        this.setState({show: this.show_});
    }

    setProgress(progress)   {
        this.setState({progress: progress});
    }

    determineActiveOptions(props)    {
        props = props || this.props;
        return Object.assign({animated: true, duration: 0.333, dim: true}, props.options, this.options);
    }

    render() {
        let show = typeof(this.props.show) === 'boolean' ? this.props.show : this.state.show > 0;
        let progress = typeof(this.props.progress) === 'number' ? this.props.progress : this.state.progress;

        let element = !show ? null : 
            <div className={'theme-indicator-controller ' + (this.state.options.dim ? 'dim ' : '') + this.props.className}>
                <OEThemeIndicatorView progress={progress}/>
            </div>;

        if(!this.state.options.animated)    return element;

        let enterTransition = {animation: 'fadeIn', duration: this.state.options.duration * 1000, easing: 'ease-in-out', begin: this.onWillAppear, complete: this.onDidAppear};
        let leaveTransition = {animation: 'fadeOut', duration: this.state.options.duration * 1000, easing: 'ease-in-out', begin: this.onWillDisappear, complete: this.onDidDisappear};

        return (
            <VelocityTransitionGroup enter={enterTransition} leave={leaveTransition}>
                {element}
            </VelocityTransitionGroup>
        );
    }

    onWillAppear()  {}

    onDidAppear()  {}

    onWillDisappear()  {}

    onDidDisappear()  {}
};

OEThemeWaitingController.defaultProps = {
    className: '',
    options: {animated: true, duration: 0.333, dim: true}
};

OEThemeWaitingController.propTypes = {
    className: PropTypes.string,
    show: PropTypes.bool,
    progress: PropTypes.number,
    options: PropTypes.shape({animated: PropTypes.bool, duration: PropTypes.number, dim: PropTypes.bool})
};

export class OEThemeWaitingControllerWindow extends React.PureComponent { 

    constructor(props) {
        super(props);

        this.show_ = 0;

        this.activeOptions = this.determineActiveOptions();

        this.state = {
            show: 0,
            progress: 0,
            options: this.activeOptions
        };

        this.onWillAppear = this.onWillAppear.bind(this);
        this.onDidAppear = this.onDidAppear.bind(this);
        this.onWillDisappear = this.onWillDisappear.bind(this);
        this.onDidDisappear = this.onDidDisappear.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if(!OEToolbox.shallowEqual(nextProps.options, this.props.options, true))  {
            this.updateActiveOptions(nextProps);
        }
    }

    show(options, complete) {
        let oldShow = this.show_;
        this.show_ += 1;

        let callComplete = false;

        retardUpdate(this, () => {

            this.setState({show: this.show_});

            if(oldShow === 0)    {
                this.setProgress(0);
                if(!OEToolbox.shallowEqual(options, this.options, true))    {
                    this.options = clone(options);
                    this.updateActiveOptions();
                }

                if(this.activeOptions.animated) {
                    this.showComplete = complete;
                } else {
                    callComplete = true;
                }
            } else {
                callComplete = true;
            }
        });

        if(callComplete && complete)    complete();
    }

    hide() {
        this.show_ = Math.max(0, this.show_ - 1);
        this.setState({show: this.show_});

        if(!this.show_ && this.showComplete)   {
            this.showComplete();
            this.showComplete = undefined;
        }
    }

    setProgress(progress)   {
        this.setState({progress: progress});
    }

    determineActiveOptions(props)    {
        props = props || this.props;
        return Object.assign({animated: true, duration: 0.333, dim: true}, props.options, this.options);
    }

    updateActiveOptions(props)  {
        let newOptions = this.determineActiveOptions(props);
        if(OEToolbox.shallowEqual(newOptions, this.activeOptions, true))    return;
        let prevOptions = this.activeOptions;
        this.activeOptions = newOptions;
        this.setState({options: this.activeOptions});
        this.onActiveOptionsChanged(prevOptions);
    }

    onActiveOptionsChanged(prevOptions) {
        if(this.activeOptions.animated != prevOptions.animated && this.showComplete) {
            this.showComplete();
            this.showComplete = undefined;
        }
    }

    render() {
        let show = typeof(this.props.show) === 'boolean' ? this.props.show : this.state.show > 0;
        if(!show) return null;

        let progress = typeof(this.props.progress) === 'number' ? this.props.progress : this.state.progress;

        let style = {
            pointerEvents: show ? this.props.pointerEvents : 'none'
        };

        let enterTransition = !this.state.options.animated ? null : {animation: 'fadeIn', duration: this.state.options.duration * 1000, easing: 'ease-in-out', begin: this.onWillAppear, complete: this.onDidAppear};
        let leaveTransition = !this.state.options.animated ? null : {animation: 'fadeOut', duration: this.state.options.duration * 1000, easing: 'ease-in-out', begin: this.onWillDisappear, complete: this.onDidDisappear};

        return(
            <OEWindowComponent
                className={'theme-indicator-controller-window ' + this.props.className}
                portalClassName={'theme-indicator-controller-window-portal'}
                style={style}
                enterTransition={enterTransition}
                leaveTransition={leaveTransition}
            > 
                <div className={'theme-indicator-controller ' + (this.state.options.dim ? 'dim ' : '') + this.props.className}>
                    <OEThemeIndicatorView progress={progress}/>
                </div>;
            </OEWindowComponent>
        );
    }

    onWillAppear()  {
        if(this.props.onWillAppear) this.props.onWillAppear();
    }

    onDidAppear()  {
        if(this.props.onDidAppear) this.props.onDidAppear();
        if(this.showComplete)   {
            this.showComplete();
            this.showComplete = undefined;
        }
    }

    onWillDisappear()  {
        if(this.props.onWillDisappear) this.props.onWillDisappear();
    }

    onDidDisappear()  {
        if(this.props.onDidDisappear) this.props.onDidDisappear();
    }
}

OEThemeWaitingControllerWindow.defaultProps = {
    className: '',
    options: {animated: true, duration: 0.333, dim: true},
    pointerEvents: 'initial'
};

OEThemeWaitingControllerWindow.propTypes = {
    className: PropTypes.string,
    show: PropTypes.bool,
    progress: PropTypes.number,
    options: PropTypes.shape({animated: PropTypes.bool, duration: PropTypes.number, dim: PropTypes.bool}),
    pointerEvents: PropTypes.string,
    onWillAppear: PropTypes.func,
    onDidAppear: PropTypes.func,
    onWillDisappear: PropTypes.func,
    onDidDisappear: PropTypes.func
};